import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Calculator, Loader2, Sparkles } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const FINISHES = [
  { v: "eco", label: "Économique" },
  { v: "standard", label: "Standard" },
  { v: "premium", label: "Premium" },
];
const REGIONS = [
  { v: "paca", label: "PACA" },
  { v: "idf", label: "Île-de-France" },
  { v: "ouest", label: "Ouest" },
  { v: "sud_ouest", label: "Sud-Ouest" },
  { v: "est", label: "Est" },
  { v: "nord", label: "Nord" },
  { v: "autre", label: "Autre" },
];
const COLORS = ["#0D6EFD", "#00C3A7", "#8F9CAE"];

export const QuoteEstimator = () => {
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({ trade: "renovation_globale", surface: 80, finish: "standard", region: "paca" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/estimate/trades").then(({ data }) => setTrades(data)).catch((err) => console.error("Failed to load trades:", err));
  }, []);

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/estimate", { ...form, surface: Number(form.surface) });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";
  const selectedUnit = trades.find((t) => t.id === form.trade)?.unit || "m²";
  const UNIT_LABELS = { "m²": "Surface (m²)", "m³": "Volume (m³)", "ml": "Longueur (ml)", "U": "Quantité (nombre)" };
  const quantityLabel = UNIT_LABELS[selectedUnit] || "Quantité";

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="quote-estimator-tool">
      <div className="space-y-4">
        <div>
          <Label className="text-white/70">Type de travaux</Label>
          <Select value={form.trade} onValueChange={(v) => setForm({ ...form, trade: v })}>
            <SelectTrigger data-testid="quote-estimator-trade-select" className="bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/70">{quantityLabel}</Label>
          <Input type="number" min="1" data-testid="quote-estimator-surface-input"
            value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })}
            className="bg-white/5 border-white/10 text-white" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/70">Finition</Label>
            <Select value={form.finish} onValueChange={(v) => setForm({ ...form, finish: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FINISHES.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/70">Région</Label>
            <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={calc} disabled={loading} data-testid="quote-estimator-calc-btn"
          className="w-full bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full h-11">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calcul…</> : <><Calculator className="w-4 h-4 mr-2" /> Estimer mon budget</>}
        </Button>
      </div>

      <div className="rounded-2xl bg-[#121A29] border border-white/10 p-5 flex flex-col justify-center">
        {!result ? (
          <div className="text-center text-white/40 text-sm py-10">
            Renseignez votre projet pour obtenir une estimation instantanée.
          </div>
        ) : (
          <div className="idb-fade-up">
            <p className="text-white/50 text-xs uppercase tracking-wider">Budget estimatif — {result.trade_name}</p>
            <p className="text-3xl font-extrabold text-white mt-1" data-testid="quote-estimator-result-price">
              {fmt(result.min)} <span className="text-white/40 text-xl">à</span> {fmt(result.max)}
            </p>
            <p className="text-white/40 text-xs mt-1">Fourchette indicative pour {result.surface} {result.unit || "m²"}</p>

            <div className="flex items-center gap-4 mt-4">
              <div className="w-24 h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={result.breakdown} dataKey="pct" innerRadius={26} outerRadius={44} paddingAngle={2}>
                      {result.breakdown.map((b) => <Cell key={b.label} fill={COLORS[result.breakdown.indexOf(b) % COLORS.length]} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 flex-1">
                {result.breakdown.map((b, i) => (
                  <div key={b.label} className="flex items-center gap-2 text-xs text-white/70">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    {b.label} <span className="ml-auto font-semibold text-white">{b.pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {result.ai_note && (
              <div className="mt-4 rounded-xl bg-[#0D6EFD]/10 border border-[#0D6EFD]/20 p-3 flex gap-2">
                <Sparkles className="w-4 h-4 text-[#00C3A7] shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm leading-relaxed">{result.ai_note}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
